import { useEffect, useState } from 'react';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import LoadingScreen from '../LoadingScreen';
import RecentActivitiesNoData, { GetNoDataTile, INoDataTileMessage } from 'Feature/PageContent/Shared/Tiles/NoDataTile';
import { getPremiumAuditLink } from 'Foundation/Configurations/GlobalConfiguration';
import { Link } from '@lmig/lmds-react-link';
import { useSessionStorage } from 'Utilities/UseStorage';
import { useUserData } from 'Foundation/User/UserContext';
import IconInfo from '@lmig/lmds-react-icons/dist/icons/Info';
import { Tooltip } from '@lmig/lmds-react-tooltip';
import './SCPremiumAudit.scss';
import { IconErrorAlert } from '@lmig/lmds-react-icons';
import { ErrorBoundary } from 'react-error-boundary';

interface PremiumAudit {
  agencyName: string;
  insuredName: string;
  account: string;
  lob: string;
  policyNumber: string;
  status: string;
  statusDesc: string;
  revisedIndicator: string;
  auditPeriod: string;
  auditStartDate: Date;
  auditEndDate: Date;
  auditType: string;
  audReqId: number;
  nocDate: Date;
  statusDate: Date;
  currentPhase: string;
  currentPhaseDate: Date;
  auditReqNonCp: string;
}
interface PremiumAuditResponse {
  Audits: PremiumAudit[] | null;
}

export default function SCPremiumAudit(props: { activeTab: number }) {
  const { userData } = useUserData();
  const premiumAuditLink = getPremiumAuditLink();
  const [noDataTile, setNoDataTile] = useState<INoDataTileMessage>({
    leftText: '<div>Loading...</div>',
    rightText: '<div>Loading...</div>',
  });
  const expirationMinutes = 0.5;
  const [premiumAudit, setPremiumAudit] = useSessionStorage<{
    premiumAuditResponse?: PremiumAuditResponse | null;
    isLoaded: boolean;
  }>('sc-premium-audit-session:' + userData?.dpecid, { isLoaded: false }, expirationMinutes);
  const [customerInfoNeededCount, setCustomerInfoNeededCount] = useState<number | null>(null);
  const [nonCompliantCount, setNonCompliantCount] = useState<number | null>(null);

  const normalizeStatus = (status: string): string => {
    return status.replace(/\s+/g, '').trim().toLowerCase();
  };

  const STATUS = {
    AUDIT_UPCOMING: 'auditupcoming',
    ASSIGNED_TO_AUDITOR: 'assignedtoauditor',
    AUDIT_COMPLETE: 'auditcomplete',
    NON_COMPLIANT: 'non-compliant',
    CUSTOMER_INFO_NEEDED: [
      'voluntaryreport1strequest',
      'voluntaryreport2ndrequest',
      'voluntaryreport3rdrequest',
      'auditorcontact#1',
      'auditorcontact#2',
      'auditorcontact#3',
    ],
  };

  const filterAuditsByStatus = (audits: PremiumAudit[], statusValues: string | string[]): PremiumAudit[] => {
    const statuses = Array.isArray(statusValues) ? statusValues : [statusValues];
    return audits.filter((audit) => statuses.includes(normalizeStatus(audit.status)));
  };

  useEffect(() => {
    const fetchData = async () => {
      let response = (await SitecoreFetch(`/api/sitecore/PremiumAuditAsync/GetPremiumAudit`)) as IServerResponse;
      let result: PremiumAuditResponse | null | undefined = premiumAudit?.premiumAuditResponse;

      if (response.Status.Code === HttpStatusCodes.OK) {
        result = response.Data as PremiumAuditResponse;
      }

      setPremiumAudit({ data: { premiumAuditResponse: result, isLoaded: true } });
    };

    try {
      if (userData?.isAgent() || userData?.isMockingAgent) {
        if (!premiumAudit?.isLoaded && !premiumAudit?.premiumAuditResponse) fetchData();
      } else {
        setPremiumAudit({ data: { premiumAuditResponse: null, isLoaded: true } });
      }
    } catch (error) {
      console.error(`SCPremiumAudit error: ${error}`);
    }
  }, []);

  useEffect(() => {
    if (premiumAudit?.isLoaded && premiumAudit?.premiumAuditResponse?.Audits) {
      if (customerInfoNeededCount === null || nonCompliantCount === null) {
        const audits = premiumAudit.premiumAuditResponse.Audits;
        if (customerInfoNeededCount === null)
          setCustomerInfoNeededCount(filterAuditsByStatus(audits, STATUS.CUSTOMER_INFO_NEEDED).length);
        if (nonCompliantCount === null) setNonCompliantCount(filterAuditsByStatus(audits, STATUS.NON_COMPLIANT).length);
      }
    } else {
      GetNoDataTile('premium audit', setNoDataTile);
    }
  }, [premiumAudit?.isLoaded, premiumAudit?.premiumAuditResponse?.Audits, customerInfoNeededCount, nonCompliantCount]);

  return (
    <ErrorBoundary fallback={<div style={{ display: 'none' }}>{`ERROR in: SCPPremiumAudit`}</div>}>
      <div
        data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
        className="recent-activities-sc-premium-audit"
      >
        {!premiumAudit?.isLoaded ? (
          <LoadingScreen />
        ) : (
          <>
            {premiumAudit?.premiumAuditResponse?.Audits && premiumAudit.premiumAuditResponse.Audits?.length > 0 ? (
              <>
                {premiumAuditLink && premiumAuditLink.length > 0 ? (
                  <div className="premium-audit-tab-link indented">
                    <Link variant="standalone" href={premiumAuditLink} target="_blank" rel="noopener noreferrer">
                      Go to premium audit dashboard
                    </Link>
                  </div>
                ) : (
                  <></>
                )}
                <div key={props.activeTab} className="premium-audit-tab-content tab-content">
                  <div className="premium-audit-tab-column">
                    <div className="premium-audit-labels-column">
                      <div className="premium-audit-labels-column-item audit-row">
                        <div className="premium-audit-type-label audit-type">
                          <div className="audit-type-text">
                            <span className="audit-type-tooltip-container">Audits upcoming</span>
                            <span className="audit-type-tooltip-container">
                              <ErrorBoundary
                                fallback={
                                  <div
                                    style={{ display: 'none' }}
                                  >{`ERROR in: SCPPremiumAudit: Tooltip Audits Upcoming`}</div>
                                }
                              >
                                <Tooltip
                                  className="audit-type-tooltip-audits-upcoming"
                                  content={
                                    <div>
                                      Audits are scheduled<div>but haven't begun</div>
                                    </div>
                                  }
                                  direction="above"
                                  align="start"
                                >
                                  <span>
                                    <IconInfo
                                      className="audit-tooltip-icon-info-audits-upcoming recent-activities-sc-premium-audit-tooltip"
                                      size="16"
                                      color="teal"
                                    />
                                  </span>
                                </Tooltip>
                              </ErrorBoundary>
                            </span>
                          </div>
                        </div>
                        <div className="premium-audit-totals-column">
                          <div className="premium-audit-total-text audit-total" style={{ fontWeight: 'initial' }}>
                            {
                              filterAuditsByStatus(premiumAudit.premiumAuditResponse.Audits, STATUS.AUDIT_UPCOMING)
                                .length
                            }
                          </div>
                        </div>
                      </div>

                      <div className="premium-audit-labels-column-item audit-row">
                        <div className="premium-audit-type-label audit-type">
                          <div className="audit-type-text">
                            <span className="audit-type-tooltip-container">Customer information needed</span>
                            <span className="audit-type-tooltip-container">
                              <ErrorBoundary
                                fallback={
                                  <div
                                    style={{ display: 'none' }}
                                  >{`ERROR in: SCPPremiumAudit: Tooltip Customer Info Needed`}</div>
                                }
                              >
                                <Tooltip
                                  className="audit-tooltip-info-needed"
                                  content={
                                    <div>
                                      Materials from the customer<div>are required to proceed</div>
                                    </div>
                                  }
                                  direction="above"
                                  align="start"
                                >
                                  <span>
                                    <IconInfo
                                      className="audit-tooltip-icon-info-info-needed recent-activities-sc-premium-audit-tooltip"
                                      size="16"
                                      color="teal"
                                    />
                                  </span>
                                </Tooltip>
                              </ErrorBoundary>
                            </span>
                          </div>
                        </div>
                        <div className="premium-audit-totals-column">
                          {customerInfoNeededCount && customerInfoNeededCount > 0 ? (
                            <div className="premium-audit-alert">
                              <IconErrorAlert
                                className="premium-audit-icon-alert"
                                style={{ marginRight: '8px' }}
                                size="16"
                                color="negative"
                              ></IconErrorAlert>
                            </div>
                          ) : (
                            <></>
                          )}
                          <div className="premium-audit-total-text audit-total" style={{ fontWeight: 'initial' }}>
                            {customerInfoNeededCount}
                          </div>
                        </div>
                      </div>

                      <div className="premium-audit-labels-column-item audit-row">
                        <div className="premium-audit-type-label audit-type">
                          <div className="audit-type-text">Audit in progress</div>
                        </div>
                        <div className="premium-audit-totals-column">
                          <div className="premium-audit-total-text audit-total" style={{ fontWeight: 'initial' }}>
                            {
                              filterAuditsByStatus(premiumAudit.premiumAuditResponse.Audits, STATUS.ASSIGNED_TO_AUDITOR)
                                .length
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="premium-audit-tab-column">
                    <div className="premium-audit-labels-column">
                      <div className="premium-audit-labels-column-item audit-row">
                        <div className="premium-audit-type-label audit-type">
                          <div className="audit-type-text">Audit complete</div>
                        </div>
                        <div className="premium-audit-totals-column">
                          <div className="premium-audit-total-text audit-total" style={{ fontWeight: 'initial' }}>
                            {
                              filterAuditsByStatus(premiumAudit.premiumAuditResponse.Audits, STATUS.AUDIT_COMPLETE)
                                .length
                            }
                          </div>
                        </div>
                      </div>
                      <div className="premium-audit-labels-column-item audit-row">
                        <div className="premium-audit-type-label audit-type">
                          <div className="audit-type-text">
                            <span className="audit-type-tooltip-container">Non-compliant</span>
                            <span className="audit-type-tooltip-container">
                              <ErrorBoundary
                                fallback={
                                  <div
                                    style={{ display: 'none' }}
                                  >{`ERROR in: SCPPremiumAudit: Tooltip Non-Compliant`}</div>
                                }
                              >
                                <Tooltip
                                  className="recent-activities-sc-premium-audit-tooltip audit-type-tooltip-non-compliant"
                                  content={
                                    <div>
                                      Customer didn't provide<div>the required materials</div>
                                    </div>
                                  }
                                  direction="above"
                                  align="start"
                                >
                                  <span>
                                    <IconInfo
                                      className="audit-tooltip-icon-info-non-compliant recent-activities-sc-premium-audit-tooltip"
                                      size="16"
                                      color="teal"
                                    />
                                  </span>
                                </Tooltip>
                              </ErrorBoundary>
                            </span>
                          </div>
                        </div>
                        <div className="premium-audit-totals-column">
                          {nonCompliantCount && nonCompliantCount > 0 ? (
                            <div className="premium-audit-alert">
                              <IconErrorAlert
                                className="premium-audit-icon-alert"
                                style={{ marginRight: '8px' }}
                                size="16"
                                color="negative"
                              ></IconErrorAlert>
                            </div>
                          ) : (
                            <></>
                          )}
                          <div className="premium-audit-total-text audit-total" style={{ fontWeight: 'initial' }}>
                            {nonCompliantCount}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="premium-audit-tab-footer recent-activities-footer">
                  {(customerInfoNeededCount && customerInfoNeededCount > 0) ||
                  (nonCompliantCount && nonCompliantCount > 0) ? (
                    <>
                      <div className="premium-audit-alert">
                        <IconErrorAlert
                          className="premium-audit-icon-alert"
                          style={{ marginRight: '8px' }}
                          size="16"
                          color="negative"
                        ></IconErrorAlert>
                      </div>
                      This step requires your attention in the premium audit dashboard
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              </>
            ) : (
              <RecentActivitiesNoData
                messageLeft={noDataTile.leftText}
                messageRight={noDataTile.rightText}
              ></RecentActivitiesNoData>
            )}
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}
